import request from '../../util/SuperagentUtils';

export interface CreateApiTokenPayload {
  description: string | null;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  company_name: string | null;
}

export interface UpdateApiTokenPayload {
  description?: string | null;
  email?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  company_name?: string | null;
}

export interface ApiCredentialsResponse {
  key: string;
  secret: string;
  tokenExpiry?: string;
}

/**
 * Response from GET /api/api-integrations
 * Field names match backend snake_case convention (ApiIntegrationVO)
 */
export interface ExistingCredentialResponse {
  id: string;
  key_prefix: string;
  description: string | null;
  creation_date: string;
  last_used_at: string | null;
  active: boolean;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  company_name: string | null;
}

/**
 * Creates a new API token for third-party integrations
 * @param payload - The token creation data including contact info
 * @returns Promise with the created credentials (key + secret)
 */
export const createApiToken = (payload: CreateApiTokenPayload) => {
  return request.post('/api/api-integrations').send(payload);
};

/**
 * Fetches all existing API credentials for the current account
 * @returns Promise with array of existing credentials
 */
export const getExistingCredentials = () => {
  return request.get('/api/api-integrations');
};

/**
 * Updates an existing API credential's metadata (partial update)
 * Uses PATCH for partial updates per Zalando REST API Guidelines
 * @param credentialId - The ID of the credential to update
 * @param payload - The fields to update (only non-null fields will be updated)
 */
export const updateApiCredential = (credentialId: string, payload: UpdateApiTokenPayload) => {
  return request.patch(`/api/api-integrations/${credentialId}`).send(payload);
};

/**
 * Revokes an API credential (soft delete - sets active = false)
 * Uses DELETE per Zalando REST API Guidelines (verb-free URLs)
 * The record is retained for audit purposes
 * @param credentialId - The ID of the credential to revoke
 */
export const revokeApiCredential = (credentialId: string) => {
  return request.delete(`/api/api-integrations/${credentialId}`);
};

/**
 * Fetches event logs for the current account's API integrations.
 * Supports filtering by thirdPartyId, operationId, date range, and result size.
 * Date format expected: MM/DD/YYYY
 * Default range: last 7 days. Max range: 90 days.
 */
export const getEventLogs = (params: {
  thirdPartyId?: string;
  operationId?: string;
  startDate?: string;
  endDate?: string;
  size?: number;
  timezone?: string;
}) => {
  const query: Record<string, string | number> = {};
  if (params.thirdPartyId) query.thirdPartyId = params.thirdPartyId;
  if (params.operationId) query.operationId = params.operationId;
  if (params.startDate) query.startDate = params.startDate;
  if (params.endDate) query.endDate = params.endDate;
  if (params.size) query.size = params.size;
  if (params.timezone) query.timezone = params.timezone;
  return request.get('/api/api-integrations/event-log').query(query);
};

